<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { filterTypes } = useContactFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const segmentNameLocal = ref(props.segmentName);

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  emit('clearFilters');
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedSegment = () => {
  if (isConditionsValid()) {
    emit('updateSegment', filters.value, segmentNameLocal.value);
  }
};

// Helper to get attributeModel from filterTypes based on attributeKey
const getFilterTypeForKey = key => {
  return filterTypes.value.find(ft => ft.attributeKey === key);
};

// Enrich filters with attributeModel and customAttributeType from filterTypes
const enrichFiltersWithMetadata = rawFilters => {
  return rawFilters.map(filter => {
    const filterType = getFilterTypeForKey(filter.attributeKey);
    if (filterType) {
      return {
        ...filter,
        attributeModel: filterType.attributeModel || 'standard',
        customAttributeType:
          filterType.attributeModel === 'customAttributes'
            ? 'contact_attribute'
            : undefined,
      };
    }
    return filter;
  });
};

function validateAndSubmit() {
  if (!isConditionsValid()) return;

  // Enrich filters with attributeModel before dispatching
  const enrichedFilters = enrichFiltersWithMetadata(filters.value);

  store.dispatch(
    'contacts/setContactFilters',
    useSnakeCase(JSON.parse(JSON.stringify(enrichedFilters)))
  );
  emit('applyFilter', enrichedFilters);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: enrichedFilters.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

onBeforeUnmount(() => emit('close'));

// Custom outside click handler that ignores dropdown menus
const handleOutsideClick = event => {
  // Don't close if clicking on the toggle button
  const toggleBtn = document.getElementById('toggleContactsFilterButton');
  if (toggleBtn && toggleBtn.contains(event.target)) {
    return;
  }

  const target = event.target;

  // Check if clicked inside any dropdown/select element
  // This covers teleported elements that may not be children of the filter modal
  const isInsideDropdown =
    // Check for any parent with position:fixed and high z-index (teleported dropdowns)
    target.closest('[style*="position: fixed"]') ||
    // Check for common dropdown class names
    target.closest('[class*="dropdown"]') ||
    target.closest('[class*="Dropdown"]') ||
    // Check for select/combobox elements
    target.closest('[role="listbox"]') ||
    target.closest('[role="option"]') ||
    target.closest('[role="combobox"]') ||
    // Check for multiselect
    target.closest('.multiselect') ||
    target.closest('[class*="multiselect"]') ||
    // Check for common popper/portal elements
    target.closest('[data-popper-placement]') ||
    target.closest('[id*="headlessui-portal"]');

  if (isInsideDropdown) {
    return;
  }

  emit('close');
};

const outsideClickHandler = [
  handleOutsideClick,
  { ignore: ['#toggleContactsFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl min-w-96 lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isSegmentView">
      <div class="pb-6 border-b border-n-weak">
        <Input
          v-model="segmentNameLocal"
          :label="$t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
          :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex justify-between gap-2">
      <button class="add-filter-btn-link" @click="addFilter">
        {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER') }}
      </button>
      <div class="flex gap-2 flex-shrink-0">
        <button class="clear-btn" @click="resetFilter">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
        </button>
        <button
          v-if="isSegmentView"
          class="apply-btn"
          :disabled="!segmentNameLocal"
          @click="updateSavedSegment"
        >
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT') }}
        </button>
        <button v-else class="apply-btn" @click="validateAndSubmit">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.add-filter-btn-link {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-primary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

.add-filter-btn-link:hover {
  color: var(--wlab-text-secondary);
}

.clear-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.clear-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.apply-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  background: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.apply-btn:hover:not(:disabled) {
  background: #f0f0f0;
}

.apply-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
